/**
 * OF-001C — Active Workspace utility
 *
 * Single source of truth (FE-side) for "which workspace is currently active
 * for routing & guard purposes". Reads from the same localStorage keys
 * RBACContext writes to, so it stays consistent across reloads and
 * across components that initialize before RBACContext mounts.
 *
 * Priority order:
 *   1. The workspace object whose `workspace_id` matches
 *      `treatx_last_active_workspace` (when present in `treatx_rbac_workspaces`).
 *   2. The first available workspace (deterministic fallback).
 *   3. null.
 *
 * Never throws. Always returns either a workspace-shaped object or null.
 */

const WORKSPACES_KEY = 'treatx_rbac_workspaces';
const ACTIVE_KEY = 'treatx_last_active_workspace';

function safeParse(raw, fallback) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function getActiveWorkspace() {
  const list = safeParse(localStorage.getItem(WORKSPACES_KEY), []);
  if (!Array.isArray(list) || list.length === 0) return null;
  const activeId = localStorage.getItem(ACTIVE_KEY);
  const match = activeId ? list.find((w) => w && w.workspace_id === activeId) : null;
  if (match && match.is_available !== false) return match;
  // Deterministic fallback: first available workspace.
  return list.find((w) => w && w.is_available && w.default_route) || null;
}

/**
 * Returns the role_id of the active workspace (e.g. "provider_sales_rep",
 * "patient", "provider_admin").  Falls back to the legacy login-time role
 * stored at `treatx_unified_user.role` when no workspace is available.
 */
export function getActiveWorkspaceRole() {
  const ws = getActiveWorkspace();
  if (ws && ws.role_id) return ws.role_id;
  // Legacy fallback: login-time primary role.
  const u = safeParse(localStorage.getItem('treatx_unified_user'), null);
  return (u && u.role) || '';
}

/**
 * Returns the clinic_id bound to the active workspace (provider-typed
 * workspaces only).  Patient and undefined-scope workspaces return null.
 */
export function getActiveWorkspaceClinicId() {
  const ws = getActiveWorkspace();
  if (!ws || ws.type !== 'provider') return null;
  return ws.clinic_id || null;
}
